import { useId, useLayoutEffect, useRef } from "react";
import type { Task } from "@common/types/task";
import { VStack } from "@design-system/VStack/VStack";
import { useTasksBoardPanelSizes } from "../tasksSplitLayout.context";
import type { TaskBoardTask } from "../types";
import { TaskBoardColumnSwitcher } from "./TaskBoardColumnSwitcher";
import { TaskBoardCompactCard } from "./TaskBoardCompactCard";
import type { TaskBoardModel } from "./useTaskBoardModel";
import { useOpenTaskSection } from "../useOpenTaskSection";
import { useViewedTaskColumn } from "./useViewedTaskColumn";
import styles from "./TaskBoardColumnPanel.module.scss";

interface TaskBoardColumnPanelProps {
    model: Pick<
        TaskBoardModel,
        "columns" | "sectionsByColumnId" | "tasksByColumnId" | "hiddenFields"
    >;
    openTaskId: string;
    /** The `TaskColumn` ids the full card shows, in order. */
    cardFields: readonly string[];
    isCrossWorkspace: boolean;
    onOpenTask: (task: Task) => void;
}

/**
 * The narrow panel beside an open task on Board: one column of compact,
 * read-only cards under its lanes' headers, starting at the open task's
 * column, with a switcher to step through the others. The open task's card
 * is highlighted.
 */
export const TaskBoardColumnPanel = ({
    model: { columns, sectionsByColumnId, tasksByColumnId, hiddenFields },
    openTaskId,
    cardFields,
    isCrossWorkspace,
    onOpenTask,
}: Readonly<TaskBoardColumnPanelProps>) => {
    const laneIdPrefix = useId();
    const { columnId, setColumnId } = useViewedTaskColumn({
        openTaskId,
        columns,
        tasksByColumnId,
    });
    const openCardRef = useRef<HTMLDivElement>(null);
    const openTaskDependencies = useOpenTaskSection(onOpenTask, "dependencies");
    useTasksBoardPanelSizes();

    useLayoutEffect(() => {
        openCardRef.current?.scrollIntoView({ block: "nearest" });
    }, [openTaskId, columnId]);

    const column = columns.find(({ id }) => id === columnId);
    if (column === undefined) {
        return null;
    }
    const columnTasks = tasksByColumnId.get(column.id) ?? [];
    const sections = (sectionsByColumnId.get(column.id) ?? []).filter(
        (section) => section.tasks.length > 0,
    );

    const renderCards = (tasks: readonly TaskBoardTask[]) =>
        tasks.map((task) => {
            const isOpen = task.id === openTaskId;
            return (
                <li key={task.id}>
                    <TaskBoardCompactCard
                        ref={isOpen ? openCardRef : undefined}
                        task={task}
                        cardFields={cardFields}
                        hiddenFields={hiddenFields}
                        isCrossWorkspace={isCrossWorkspace}
                        isOpen={isOpen}
                        onOpen={onOpenTask}
                        onOpenDependencies={openTaskDependencies}
                    />
                </li>
            );
        });

    return (
        <div className={styles.panel}>
            <TaskBoardColumnSwitcher
                columns={columns}
                column={column}
                count={columnTasks.length}
                onColumnChange={setColumnId}
            />
            <div className={styles.scroller}>
                {sections.length === 0 && (
                    <p className={styles.empty}>No tasks</p>
                )}
                {sections.map(({ lane, tasks }, laneIndex) => {
                    const titleId = `${laneIdPrefix}-${laneIndex}`;
                    return (
                        <section key={lane?.id ?? column.id}>
                            {lane !== null && (
                                <h4 id={titleId} className={styles.laneTitle}>
                                    {lane.title}
                                    <span>{tasks.length}</span>
                                </h4>
                            )}
                            <VStack
                                as="ul"
                                gap={8}
                                paddingBlock={12}
                                paddingInline={16}
                                aria-label={
                                    lane === null ? column.title : undefined
                                }
                                aria-labelledby={
                                    lane === null ? undefined : titleId
                                }
                                className={styles.cards}
                            >
                                {renderCards(tasks)}
                            </VStack>
                        </section>
                    );
                })}
            </div>
        </div>
    );
};
